import { useEffect, useState, useCallback } from "react";
import { authAxios } from "../../utils/auth";
import { toast } from "sonner";
import { RefreshCw, ArrowRightLeft, ChevronLeft, ChevronRight, Sparkles, Download } from "lucide-react";

const cents = (c) => (c == null ? "—" : `$${(Number(c) / 100).toFixed(2)}`);

const TIER_COLORS = {
  BRONZE:   "bg-amber-700/15 text-amber-300",
  SILVER:   "bg-slate-500/15 text-slate-200",
  GOLD:     "bg-yellow-500/15 text-yellow-300",
  PLATINUM: "bg-cyan-500/15 text-cyan-300",
};

function SubscribeConversions() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;
  useEffect(() => {
    authAxios.get("/affiliate/subscribe-redemptions")
      .then((res) => {
        setRows(res.data?.redemptions || []);
        setTotal(res.data?.total_bonus || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const startIdx = (page - 1) * PAGE_SIZE;
  const pageRows = rows.slice(startIdx, startIdx + PAGE_SIZE);
  return (
    <section
      className="rounded-xl border border-white/[0.06] bg-[#111113] overflow-hidden"
      data-testid="subscribe-conversions"
    >
      <div className="px-3 py-2.5 flex items-center gap-2 border-b border-white/[0.04]">
        <Sparkles className="w-3.5 h-3.5 text-[#C5A55A]" />
        <span className="text-xs font-medium text-white/80">Subscribe Offer Conversions</span>
        <span className="ml-auto text-[11px] text-white/40 font-mono">
          {rows.length} redemption{rows.length === 1 ? "" : "s"} · {total.toLocaleString()} coins gifted
        </span>
      </div>
      {loading ? (
        <div className="py-6 text-center"><RefreshCw className="w-4 h-4 animate-spin inline text-white/40" /></div>
      ) : rows.length === 0 ? (
        <div className="text-center py-6 text-white/40 text-xs" data-testid="subscribe-conversions-empty">
          No Subscribe offer redemptions yet. When a direct customer subscribes while your offer is active, they'll appear here.
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-white/[0.02] text-white/40 uppercase tracking-wider text-[10px]">
                <tr>
                  <th className="text-left py-2 px-3 font-medium">Redeemed</th>
                  <th className="text-left py-2 px-3 font-medium">Buyer</th>
                  <th className="text-center py-2 px-3 font-medium">Tier</th>
                  <th className="text-right py-2 px-3 font-medium">Bonus Coins</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((r) => (
                  <tr key={`${r.session_id}-${r.created_at}`} className="border-t border-white/[0.04]" data-testid={`subscribe-conversion-${r.session_id}`}>
                    <td className="py-1.5 px-3 text-white/60 whitespace-nowrap">{(r.created_at || "").slice(0, 10)}</td>
                    <td className="py-1.5 px-3 text-white/85">{r.buyer_details || "—"}</td>
                    <td className="py-1.5 px-3 text-center">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${TIER_COLORS[r.tier] || "bg-white/5 text-white/60"}`}>
                        {r.tier || "—"}
                      </span>
                    </td>
                    <td className="py-1.5 px-3 text-right font-mono text-[#C5A55A]">
                      {r.bonus_coins.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-3 py-2 border-t border-white/[0.04] text-[11px] text-white/40 font-mono">
              <span data-testid="sub-conversions-range">
                {startIdx + 1}–{Math.min(startIdx + PAGE_SIZE, rows.length)} of {rows.length}
              </span>
              <div className="flex items-center gap-1">
                <span data-testid="sub-conversions-page-indicator">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="p-1 rounded hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed"
                  data-testid="sub-conversions-prev-page"
                  aria-label="Previous page"
                >
                  <ChevronLeft className="w-3 h-3" />
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="p-1 rounded hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed"
                  data-testid="sub-conversions-next-page"
                  aria-label="Next page"
                >
                  <ChevronRight className="w-3 h-3" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}

const STATUS_COLORS = {
  PENDING:   "bg-amber-500/15 text-amber-300 ring-amber-500/30",
  AVAILABLE: "bg-emerald-500/15 text-emerald-300 ring-emerald-500/30",
  SCHEDULED: "bg-sky-500/15 text-sky-300 ring-sky-500/30",
  PAID:      "bg-indigo-500/15 text-indigo-300 ring-indigo-500/30",
  FROZEN:    "bg-slate-500/15 text-slate-300 ring-slate-500/30",
  REVERSED:  "bg-red-500/15 text-red-300 ring-red-500/30",
};

const TYPE_COLORS = {
  CUSTOMER:   "bg-emerald-500/10 text-emerald-300",
  SPONSOR:    "bg-sky-500/10 text-sky-300",
  LEADERSHIP: "bg-indigo-500/10 text-indigo-300",
  REVERSAL:   "bg-red-500/10 text-red-300",
};

const TYPE_LABEL = {
  CUSTOMER:   "Direct",
  SPONSOR:    "Sponsor",
  LEADERSHIP: "Leadership",
  REVERSAL:   "Reversal",
};

const PAGE_SIZE = 50;

export default function CommissionsTab() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("all");
  const [type, setType] = useState("all");
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String((page - 1) * PAGE_SIZE),
      });
      if (status !== "all") params.set("status", status);
      if (type !== "all") params.set("commission_type", type);
      const res = await authAxios.get(`/affiliate/commissions?${params}`);
      setRows(res.data?.rows || []);
      setTotal(res.data?.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load commissions");
    } finally {
      setLoading(false);
    }
  }, [status, type, page]);

  useEffect(() => { load(); }, [load]);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [status, type]);

  // CSV export — pulls ALL rows matching the current status/type filters
  // (ignoring pagination), formats them client-side, and triggers a browser
  // download. The endpoint caps `limit` at 200 per call, so we page through
  // the results in 200-row batches until we've fetched all of them.
  const [exporting, setExporting] = useState(false);
  const exportCsv = async () => {
    setExporting(true);
    try {
      const PAGE = 200;
      const buildParams = (offset) => {
        const p = new URLSearchParams({ limit: String(PAGE), offset: String(offset) });
        if (status !== "all") p.set("status", status);
        if (type !== "all") p.set("commission_type", type);
        return p;
      };
      const first = await authAxios.get(`/affiliate/commissions?${buildParams(0)}`);
      const allRows = [...(first.data?.rows || [])];
      const totalMatching = Number(first.data?.total || allRows.length);
      // Safety cap so a corrupt total field can't spiral into runaway paging.
      const HARD_CAP = 20000;
      let offset = PAGE;
      while (offset < totalMatching && offset < HARD_CAP) {
        const next = await authAxios.get(`/affiliate/commissions?${buildParams(offset)}`);
        const batch = next.data?.rows || [];
        if (batch.length === 0) break;
        allRows.push(...batch);
        offset += PAGE;
      }
      if (allRows.length === 0) {
        toast.info("No commissions to export for the current filters");
        return;
      }
      const columns = [
        "id", "earned_at", "commission_type",
        "source_customer_name", "source_customer_id",
        "source_affiliate_name", "source_affiliate_user_id",
        "commission_rate", "gross_transaction_amount", "commission_amount",
        "status", "clears_at", "available_at", "scheduled_at", "paid_at",
        "payment_source", "subscription_id", "coin_pack_id",
      ];
      const escape = (v) => {
        if (v == null) return "";
        const s = String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const lines = [columns.join(",")];
      for (const r of allRows) lines.push(columns.map((c) => escape(r[c])).join(","));
      const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      a.href = url;
      a.download = `my-commissions-${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${allRows.length} commission${allRows.length === 1 ? "" : "s"}`);
    } catch (e) {
      // FastAPI/Pydantic 422 returns detail as an array of objects — coerce
      // to a string so <Toast> doesn't try to render the object graph.
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : Array.isArray(detail)
          ? detail.map((d) => d?.msg || JSON.stringify(d)).join("; ")
          : e?.message || "Failed to export CSV";
      toast.error(msg);
    } finally {
      setExporting(false);
    }
  };

  const startRow = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endRow = Math.min(page * PAGE_SIZE, total);

  return (
    <section className="space-y-4" data-testid="commissions-tab">
      {/* Subscribe Offer Conversions panel */}
      <SubscribeConversions />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5 text-xs text-white/40">
          <ArrowRightLeft className="w-3.5 h-3.5" />
          <span>Filter</span>
        </div>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="bg-black/40 border border-white/10 text-white/80 rounded-md h-8 px-2 text-xs focus:outline-none focus:border-[#C5A55A]/40"
          data-testid="commissions-status-filter"
        >
          <option value="all">All statuses</option>
          <option value="PENDING">Pending</option>
          <option value="AVAILABLE">Available</option>
          <option value="SCHEDULED">Scheduled</option>
          <option value="PAID">Paid</option>
          <option value="REVERSED">Reversed</option>
          <option value="FROZEN">Frozen</option>
        </select>
        <select
          value={type}
          onChange={(e) => setType(e.target.value)}
          className="bg-black/40 border border-white/10 text-white/80 rounded-md h-8 px-2 text-xs focus:outline-none focus:border-[#C5A55A]/40"
          data-testid="commissions-type-filter"
        >
          <option value="all">All types</option>
          <option value="CUSTOMER">Direct (20% / 30%)</option>
          <option value="SPONSOR">Sponsor (6%)</option>
          <option value="LEADERSHIP">Leadership (4%)</option>
          <option value="REVERSAL">Reversal</option>
        </select>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md border border-white/10 hover:border-white/20 text-white/60 hover:text-white disabled:opacity-40"
          data-testid="commissions-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
        <button
          onClick={exportCsv}
          disabled={exporting || loading}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 disabled:opacity-40"
          data-testid="commissions-export-csv"
          title="Download the full filtered commission history as CSV"
        >
          <Download className={`w-3.5 h-3.5 ${exporting ? "animate-pulse" : ""}`} />
          {exporting ? "Exporting…" : "Download CSV"}
        </button>
        <span className="ml-auto text-[11px] text-white/40 font-mono" data-testid="commissions-count">
          {total === 0
            ? "0 rows"
            : `${startRow}–${endRow} of ${total}`}
        </span>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-white/[0.06] bg-[#111113] overflow-hidden">
        {rows.length === 0 && !loading ? (
          <div className="text-center py-10 text-white/40 text-sm" data-testid="commissions-empty">
            No commissions match this filter. Share your affiliate link — every purchase your network makes will show up here.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-white/[0.02] text-white/40 uppercase tracking-wider text-[10px]">
                <tr>
                  <th className="text-left py-2 px-3 font-medium">Earned</th>
                  <th className="text-left py-2 px-3 font-medium">From</th>
                  <th className="text-right py-2 px-3 font-medium">Rate</th>
                  <th className="text-right py-2 px-3 font-medium">Sale</th>
                  <th className="text-right py-2 px-3 font-medium">Commission</th>
                  <th className="text-left py-2 px-3 font-medium">Status</th>
                  <th className="text-left py-2 px-3 font-medium">Clears / Paid</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const t = (r.commission_type || "").toUpperCase();
                  const s = (r.status || "").toUpperCase();
                  const source =
                    r.source_customer_name ||
                    r.source_affiliate_name ||
                    r.source_customer_id ||
                    "—";
                  const when = s === "PAID" ? r.paid_at : (s === "PENDING" ? r.clears_at : (r.available_at || r.scheduled_at));
                  return (
                    <tr
                      key={r.id}
                      className="border-t border-white/[0.04] hover:bg-white/[0.02]"
                      data-testid={`commission-row-${r.id}`}
                    >
                      <td className="py-1.5 px-3 text-white/60 whitespace-nowrap">
                        {r.earned_at ? new Date(r.earned_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="py-1.5 px-3 text-white/80">{source}</td>
                      <td className="py-1.5 px-3 text-right text-white/50 font-mono">
                        {r.commission_rate != null
                          ? `${(Number(r.commission_rate) * 100).toFixed(0)}%`
                          : "—"}
                      </td>
                      <td className="py-1.5 px-3 text-right text-white/50 font-mono">
                        {cents(r.gross_transaction_amount)}
                      </td>
                      <td
                        className={`py-1.5 px-3 text-right font-mono font-semibold ${
                          Number(r.commission_amount) < 0 ? "text-red-300" : "text-emerald-300"
                        }`}
                      >
                        {cents(r.commission_amount)}
                      </td>
                      <td className="py-1.5 px-3">
                        <span
                          className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ring-1 ${
                            STATUS_COLORS[s] || ""
                          }`}
                        >
                          {s}
                        </span>
                      </td>
                      <td className="py-1.5 px-3 text-white/50 font-mono text-[10px]">
                        {when ? new Date(when).toLocaleDateString() : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-3 py-2 border-t border-white/[0.04] text-[11px] text-white/40">
            <span data-testid="commissions-page-indicator">
              Page {page} of {totalPages}
            </span>
            <div className="flex gap-1">
              <button
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="p-1 rounded hover:bg-white/5 disabled:opacity-30"
                data-testid="commissions-prev-page"
                aria-label="Previous page"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              <button
                disabled={page >= totalPages || loading}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="p-1 rounded hover:bg-white/5 disabled:opacity-30"
                data-testid="commissions-next-page"
                aria-label="Next page"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
